import { createContext, useContext } from 'react';

/**
 * Registro das ações demoradas em execução. Vive fora de
 * `TasksInExecution.tsx` pelo mesmo motivo do `toastContext`: aquele arquivo
 * só exporta componentes (`react-refresh/only-export-components`).
 *
 * Até 29/08 isto era o estado de **um** modal que travava a tela. O mrolim
 * reprovou no lab («travar a tela e o usuário não poder fazer nada ficou
 * ruim»): agora é um registro de tarefas concorrentes, e o feedback é o
 * indicador do cabeçalho — a tela segue utilizável enquanto a ordem corre.
 */

/** Passo do contador do indicador: o relógio anda de segundo em segundo. */
export const WAIT_TICKMS = 1000;

export interface WaitOptions {
  /** Título da tarefa — o que está acontecendo, em português. */
  titulo?: string;
  /**
   * Teto da chamada em milissegundos, quando ela pediu um maior que o padrão do
   * cliente HTTP. O indicador anuncia esse limite no detalhe da tarefa.
   *
   * Passe o **mesmo** valor que foi ao `timeoutMs` da chamada: prometer um limite
   * diferente do que o cliente respeita é pior do que não prometer nada.
   */
  limiteMs?: number;
}

/** Uma ação demorada em voo. Sai do registro quando a promessa termina. */
export interface TaskInExecution {
  /** Sequencial da árvore; só serve de chave e de identidade na remoção. */
  id: number;
  titulo: string;
  /** `Date.now()` do disparo — o contador é derivado dele, não guardado. */
  inicio: number;
  /** Teto anunciado, ou `null` quando a ação usa o padrão do cliente. */
  limiteMs: number | null;
  /**
   * Chave de uma tarefa **acompanhada** (`manter`/`soltar`), ou `null` para as
   * que nasceram de uma promessa (`executar`). Ver `manter`.
   */
  chave: string | null;
}

export interface ActionSlowAPI {
  /**
   * Registra a tarefa, espera a promessa e devolve o que ela devolveu.
   *
   * **Rejeição passa adiante.** A tarefa sai do registro e o erro continua
   * subindo para quem chamou — o banner da tela é que mostra o código (FR-123).
   * Nada de mensagem duplicada aqui dentro.
   */
  executar: <T>(fn: () => Promise<T>, opcoes?: WaitOptions) => Promise<T>;
  /**
   * Põe no indicador uma tarefa que **não é uma promessa desta aba** — o
   * discovery, desde 29/08: o `POST` responde `202` na hora e o que dura é a
   * varredura no servidor, acompanhada por polling. Também é assim que aparece
   * a varredura que outra pessoa (ou o periódico) começou.
   *
   * **Idempotente por `chave`**: chamar de novo com a mesma chave não duplica a
   * tarefa nem reinicia o relógio — é o que deixa duas telas montadas ao mesmo
   * tempo (a moldura e o botão) acompanharem a mesma varredura sem o indicador
   * dizer «Tarefas em execução (2)» sobre uma só.
   *
   * Sem toast de fecho: quem acompanha tem mensagem própria e mais específica
   * («3 imagens varridas · …»), e duas seguidas só empurram a que importa.
   */
  manter: (chave: string, opcoes?: WaitOptions) => void;
  /** Tira do indicador a tarefa de `chave`. Chave desconhecida é no-op. */
  soltar: (chave: string) => void;
  /** As tarefas em voo agora, na ordem em que começaram. */
  tarefas: readonly TaskInExecution[];
}

export const WaitContext = createContext<ActionSlowAPI | null>(null);

/**
 * Ação demorada com feedback (o «Discovery agora» chega a ~15 s no lab).
 * Exige um `<EsperaProvider>` acima — ele mora no `App` e nos helpers de teste.
 */
export function useActionSlow(): ActionSlowAPI {
  const api = useContext(WaitContext);
  if (!api) throw new Error('useAcaoDemorada: falta um <EsperaProvider> acima na árvore.');
  return api;
}
